import React, { useState } from 'react';
import { AssetBYODPolicy, assetAPI } from '../../services/assetAPI';
import {
  Cloud,
  Shield,
  Lock,
  Smartphone,
  Clock,
  CheckCircle,
  XCircle,
  Edit2,
  Save,
  X,
  MapPin
} from 'lucide-react';

interface BYODPolicyViewProps {
  policy: AssetBYODPolicy;
  onUpdate: () => void;
}

const BYODPolicyView: React.FC<BYODPolicyViewProps> = ({ policy, onUpdate }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState(policy);
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await assetAPI.updateBYODPolicy(editData);
      setIsEditing(false);
      onUpdate();
    } catch (error) {
      console.error('Failed to update BYOD policy:', error);
    } finally {
      setIsSaving(false);
    }
  };

  if (isEditing) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold text-gray-900">Edit BYOD Policy</h2>
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                setIsEditing(false);
                setEditData(policy);
              }}
              className="px-3 py-2 text-sm text-gray-600 hover:text-gray-900"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50"
            >
              <Save size={16} />
              {isSaving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>

        <div className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Policy Name</label>
              <input
                type="text"
                value={editData.policy_name}
                onChange={(e) => setEditData({ ...editData, policy_name: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Version</label>
              <input
                type="text"
                value={editData.policy_version}
                onChange={(e) => setEditData({ ...editData, policy_version: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Policy Summary</label>
            <textarea
              value={editData.policy_summary || ''}
              onChange={(e) => setEditData({ ...editData, policy_summary: e.target.value })}
              rows={3}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Security Model</label>
            <textarea
              value={editData.security_model || ''}
              onChange={(e) => setEditData({ ...editData, security_model: e.target.value })}
              rows={3}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Enforcement Details</label>
            <textarea
              value={editData.enforcement_details || ''}
              onChange={(e) => setEditData({ ...editData, enforcement_details: e.target.value })}
              rows={3}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={editData.cloud_first_enabled}
                onChange={(e) => setEditData({ ...editData, cloud_first_enabled: e.target.checked })}
                className="h-4 w-4 text-blue-600 rounded border-gray-300"
              />
              <span className="text-sm text-gray-700">Cloud-First Enabled</span>
            </label>

            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={editData.mfa_required}
                onChange={(e) => setEditData({ ...editData, mfa_required: e.target.checked })}
                className="h-4 w-4 text-blue-600 rounded border-gray-300"
              />
              <span className="text-sm text-gray-700">MFA Required</span>
            </label>

            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={editData.local_data_storage_allowed}
                onChange={(e) => setEditData({ ...editData, local_data_storage_allowed: e.target.checked })}
                className="h-4 w-4 text-blue-600 rounded border-gray-300"
              />
              <span className="text-sm text-gray-700">Local Data Storage Allowed</span>
            </label>

            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={editData.mdm_required}
                onChange={(e) => setEditData({ ...editData, mdm_required: e.target.checked })}
                className="h-4 w-4 text-blue-600 rounded border-gray-300"
              />
              <span className="text-sm text-gray-700">MDM Required</span>
            </label>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Session Timeout (minutes)</label>
            <input
              type="number"
              value={editData.session_timeout_minutes}
              onChange={(e) => setEditData({ ...editData, session_timeout_minutes: parseInt(e.target.value) })}
              className="w-full max-w-xs border border-gray-300 rounded-lg px-3 py-2 text-sm"
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Policy Header */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-blue-50 rounded-lg">
              <Cloud className="text-blue-600" size={24} />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">{policy.policy_name}</h2>
              <p className="text-sm text-gray-500">
                Version {policy.policy_version} | Effective: {policy.effective_date}
              </p>
            </div>
          </div>
          <button
            onClick={() => setIsEditing(true)}
            className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg"
          >
            <Edit2 size={16} />
            Edit Policy
          </button>
        </div>

        {policy.policy_summary && (
          <div className="mt-4 p-4 bg-blue-50 rounded-lg">
            <p className="text-sm text-blue-800">{policy.policy_summary}</p>
          </div>
        )}
      </div>

      {/* Configuration Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Cloud size={18} className="text-blue-500" />
              <span className="text-sm font-medium text-gray-700">Cloud-First</span>
            </div>
            {policy.cloud_first_enabled ? (
              <CheckCircle size={18} className="text-green-500" />
            ) : (
              <XCircle size={18} className="text-red-500" />
            )}
          </div>
          <p className="text-xs text-gray-500 mt-2">
            {policy.cloud_first_enabled ? 'All data resides in cloud infrastructure' : 'Not enforced'}
          </p>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Lock size={18} className="text-blue-500" />
              <span className="text-sm font-medium text-gray-700">MFA Required</span>
            </div>
            {policy.mfa_required ? (
              <CheckCircle size={18} className="text-green-500" />
            ) : (
              <XCircle size={18} className="text-red-500" />
            )}
          </div>
          <p className="text-xs text-gray-500 mt-2">
            {policy.mfa_required ? 'Required at platform level' : 'Not required'}
          </p>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Smartphone size={18} className="text-blue-500" />
              <span className="text-sm font-medium text-gray-700">MDM Required</span>
            </div>
            {policy.mdm_required ? (
              <CheckCircle size={18} className="text-green-500" />
            ) : (
              <span className="text-xs text-gray-500">Not Required</span>
            )}
          </div>
          <p className="text-xs text-gray-500 mt-2">
            {policy.mdm_required ? 'Mobile Device Management required' : 'Cloud-first eliminates endpoint risks'}
          </p>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Clock size={18} className="text-blue-500" />
              <span className="text-sm font-medium text-gray-700">Session Timeout</span>
            </div>
          </div>
          <p className="text-2xl font-semibold text-gray-900 mt-1">{policy.session_timeout_minutes} min</p>
        </div>
      </div>

      {/* Security Model */}
      {policy.security_model && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center gap-2 mb-4">
            <Shield className="text-green-600" size={20} />
            <h3 className="text-lg font-semibold text-gray-900">Security Model</h3>
          </div>
          <p className="text-sm text-gray-700 whitespace-pre-line">{policy.security_model}</p>
        </div>
      )}

      {/* Enforcement Details */}
      {policy.enforcement_details && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center gap-2 mb-4">
            <Lock className="text-blue-600" size={20} />
            <h3 className="text-lg font-semibold text-gray-900">Enforcement Details</h3>
          </div>
          <p className="text-sm text-gray-700 whitespace-pre-line">{policy.enforcement_details}</p>
        </div>
      )}

      {/* Approved Data Locations */}
      {policy.approved_data_locations && policy.approved_data_locations.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center gap-2 mb-4">
            <MapPin className="text-purple-600" size={20} />
            <h3 className="text-lg font-semibold text-gray-900">Approved Data Locations</h3>
          </div>
          <div className="flex flex-wrap gap-2">
            {policy.approved_data_locations.map((location, idx) => (
              <span
                key={idx}
                className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-purple-50 text-purple-700"
              >
                {location}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Local Storage Warning */}
      {!policy.local_data_storage_allowed && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <CheckCircle className="text-green-600 flex-shrink-0 mt-0.5" size={20} />
            <div>
              <h4 className="text-sm font-medium text-green-800">No Local Data Storage</h4>
              <p className="text-sm text-green-700 mt-1">
                Customer data is never stored on local employee devices. This cloud-first architecture
                eliminates endpoint security risks while maintaining strong access controls.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BYODPolicyView;
